import classNames from 'classnames';
import {useEffect, useRef, useState} from 'react';
import ReactCrop, {Crop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import DotsLoading from '../dotsLoading';

interface Props {
  originalFile: File;
  onComplete: (croppedFile: File) => void;
  onClose: () => void;
}

const ImageCropModal = ({originalFile, onComplete, onClose}: Props) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100, // 100% to cover the full image by default
    height: 100, // 100% to cover the full image by default
    x: 0, // 0 to start from the beginning
    y: 0, // 0 to start from the beginning
  });

  const [imageSrc, setImageSrc] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);

  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const reader = new FileReader();

    reader.onloadend = () => {
      setImageSrc(reader.result as string);
      setLoading(false);
    };

    reader.readAsDataURL(originalFile);
  }, [originalFile]);

  const getCroppedImg = async (): Promise<File | null> => {
    const image = imageRef.current;
    // Use completedCrop if available, otherwise fall back to the current crop
    const currentCrop = completedCrop || crop;

    if (!image || !currentCrop.width || !currentCrop.height) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Handle both pixel and percentage units
    let cropPx;
    if (currentCrop.unit === '%') {
      // Convert percentage to pixels
      cropPx = {
        x: (currentCrop.x! * image.naturalWidth) / 100,
        y: (currentCrop.y! * image.naturalHeight) / 100,
        width: (currentCrop.width! * image.naturalWidth) / 100,
        height: (currentCrop.height! * image.naturalHeight) / 100,
      };
    } else {
      // Already in pixels, just scale
      cropPx = {
        x: currentCrop.x! * scaleX,
        y: currentCrop.y! * scaleY,
        width: currentCrop.width! * scaleX,
        height: currentCrop.height! * scaleY,
      };
    }

    // Ensure crop coordinates are within image bounds
    cropPx.x = Math.max(0, Math.min(cropPx.x, image.naturalWidth - 1));
    cropPx.y = Math.max(0, Math.min(cropPx.y, image.naturalHeight - 1));
    cropPx.width = Math.max(
      1,
      Math.min(cropPx.width, image.naturalWidth - cropPx.x),
    );
    cropPx.height = Math.max(
      1,
      Math.min(cropPx.height, image.naturalHeight - cropPx.y),
    );

    // Calculate the scale factor to maintain reasonable size while preserving quality
    const MAX_DIMENSION = 800;
    const scaleResize = Math.min(
      1,
      MAX_DIMENSION / Math.max(cropPx.width, cropPx.height),
    );

    const outputWidth = Math.round(cropPx.width * scaleResize);
    const outputHeight = Math.round(cropPx.height * scaleResize);

    // Ensure minimum dimensions to prevent tiny images
    const MIN_DIMENSION = 100;
    const finalWidth = Math.max(outputWidth, MIN_DIMENSION);
    const finalHeight = Math.max(outputHeight, MIN_DIMENSION);

    const canvas = document.createElement('canvas');
    canvas.width = finalWidth;
    canvas.height = finalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Use high-quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      cropPx.x,
      cropPx.y,
      cropPx.width,
      cropPx.height,
      0,
      0,
      finalWidth,
      finalHeight,
    );

    return new Promise(resolve => {
      canvas.toBlob(
        blob => {
          if (blob) {
            const compressedFile = new File(
              [blob],
              originalFile.name.replace(/\..+$/, '.jpeg'),
              {type: 'image/jpeg'},
            );
            resolve(compressedFile);
          } else {
            resolve(null);
          }
        },
        'image/jpeg',
        0.85,
      );
    });
  };

  const handleDone = async () => {
    const cropped = await getCroppedImg();
    if (cropped) onComplete(cropped);
  };

  // Handle crop completion to get the final crop coordinates
  const onCropComplete = (crop: Crop) => {
    setCompletedCrop(crop);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white p-4 rounded-2xl w-[90%] max-w-4xl">
        <div className="w-full relative h-fit max-h-[80vh] overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-28">
              <DotsLoading />
            </div>
          )}
          {!loading && (
            <div className="flex justify-center">
              <ReactCrop
                crop={crop}
                onChange={setCrop}
                onComplete={onCropComplete}
                keepSelection
                style={{maxWidth: '100%', maxHeight: '70vh'}}
              >
                <img
                  ref={imageRef}
                  src={imageSrc}
                  alt="Crop"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    display: 'block',
                  }}
                  onLoad={() => {
                    setCrop({
                      unit: '%',
                      width: 100,
                      height: 100,
                      x: 0,
                      y: 0,
                    });
                  }}
                />
              </ReactCrop>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2 mt-4">
          <button
            className={classNames(
              'w-full text-farm-shop-500 bg-transparent h-12 rounded mb-3 border border-farm-shop-500',
            )}
            onClick={onClose}
          >
            لغو
          </button>
          <button
            className={classNames(
              'w-full text-white h-12 rounded mb-3 bg-farm-shop-500',
            )}
            type="button"
            onClick={handleDone}
          >
            تایید
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
